import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Checkbox, DefaultButton, Icon, PrimaryButton, TooltipHost } from "@fluentui/react";
import { t } from "i18next";
import themeContext from "../../../../context/theme/ThemeContext";
import "./libraryTag.css";

const UNCATEGORIZED_ID = "uncategorized";
const UNTAGGED = "untagged";
const nameTipStyles = { root: { display: "block", minWidth: 0, overflow: "hidden", flex: 1 } };

const NameTip = ({ id, name, className }) => {
  const full = name == null ? "" : String(name);
  return (
    <TooltipHost content={full} id={id} styles={nameTipStyles}>
      <span className={className}>{full}</span>
    </TooltipHost>
  );
};

const catState = (category, sel) => {
  const n = category.tagIds.length;
  const nin = category.tagIds.filter((id) => sel[id] === "in").length;
  const nex = category.tagIds.filter((id) => sel[id] === "ex").length;
  const all = n > 0 && nin === n;
  return { n, nin, nex, all, mixed: !all && (nin > 0 || nex > 0) };
};

const buildView = (tags, categories, uncategorizedName) => {
  const used = new Set();
  categories.forEach((category) => category.tagIds.forEach((id) => used.add(id)));
  const sorted = [...categories].sort((a, b) => a.name.localeCompare(b.name));
  return [
    {
      id: UNCATEGORIZED_ID,
      name: uncategorizedName,
      tagIds: tags.filter((tag) => !used.has(tag.id)).map((tag) => tag.id),
      virtual: true,
    },
    ...sorted.map((category) => ({ ...category, virtual: false })),
  ];
};

const checkStyles = {
  root: { margin: 0, pointerEvents: "none" },
  label: { margin: 0, padding: 0, alignItems: "center" },
  checkbox: { margin: 0 },
  text: { display: "none" },
};

const Check = ({ all, mixed, disabled }) => (
  <Checkbox checked={!!all} indeterminate={!!mixed} disabled={!!disabled} styles={checkStyles} />
);

const LibraryCategoryFilter = ({ tags, categories, sel, setSel }) => {
  const { themeName, getScrollBarStyleForTheme } = useContext(themeContext);
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [hoverId, setHoverId] = useState(null);
  const [draft, setDraft] = useState({});
  const scrollClass = getScrollBarStyleForTheme(themeName, "common-modal-scroll");
  const uncategorizedName = t("study-list.library-tag.uncategorized");
  const view = useMemo(
    () => buildView(tags, categories, uncategorizedName),
    [tags, categories, uncategorizedName]
  );
  const tagById = useMemo(() => {
    const map = new Map();
    tags.forEach((tag) => map.set(tag.id, tag));
    return map;
  }, [tags]);

  const hovered = hoverId === UNTAGGED
    ? { id: UNTAGGED }
    : view.find((category) => category.id === hoverId) || view.find((category) => category.tagIds.length) || view[0];

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (event) => {
      if (anchorRef.current && !anchorRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const onKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const toggleUntagged = () => {
    setDraft((prev) => (prev[UNTAGGED] === "in" ? {} : { [UNTAGGED]: "in" }));
  };

  const toggleCategory = (category) => {
    if (!category?.tagIds.length) return;
    setDraft((prev) => {
      const next = { ...prev };
      delete next[UNTAGGED];
      if (catState(category, prev).all) {
        category.tagIds.forEach((id) => delete next[id]);
      } else {
        category.tagIds.forEach((id) => {
          next[id] = "in";
        });
      }
      return next;
    });
  };

  const toggleInclude = (tagId) => {
    setDraft((prev) => {
      const next = { ...prev };
      delete next[UNTAGGED];
      if (next[tagId] === "in") delete next[tagId];
      else next[tagId] = "in";
      return next;
    });
  };

  const openMenu = () => {
    if (open) {
      setOpen(false);
      return;
    }
    setDraft({ ...sel });
    const first = view.find((category) => category.tagIds.length) || view[0];
    setHoverId(sel[UNTAGGED] === "in" ? UNTAGGED : first ? first.id : null);
    setOpen(true);
  };

  const applyFilter = () => {
    setSel(draft);
    setOpen(false);
  };

  const clearFilter = () => {
    setDraft({});
    setSel({});
    setOpen(false);
  };

  const hoveredTags = (hovered?.tagIds || [])
    .map((id) => tagById.get(id))
    .filter(Boolean)
    .sort((a, b) => a.name.localeCompare(b.name));
  const hoveredState = hovered && hovered.tagIds ? catState(hovered, draft) : null;
  const menuBackground = { backgroundColor: "var(--studylist-filter-popup-background)" };

  return (
    <div className="library-filter-pills">
      <div className={`library-filter-anchor${open ? " is-open" : ""}`} ref={anchorRef}>
        <button
          type="button"
          className={`library-filter-all${open ? " is-open" : ""}`}
          aria-expanded={open}
          onClick={openMenu}
        >
          {t("study-list.library-tag.all-categories")}
          <span className={`library-filter-chevron${open ? " is-open" : ""}`}>
            <Icon iconName="ChevronDown" />
          </span>
        </button>
        {open && hovered ? (
          <div
            className="library-filter-menu"
            role="dialog"
            aria-label={t("study-list.library-tag.all-categories")}
            style={menuBackground}
          >
            <div className="library-filter-menu-body">
            <div className="library-filter-menu-cats" style={menuBackground}>
              <div className="library-filter-menu-heading">{t("study-list.library-tag.categories")}</div>
              <div className={`library-filter-menu-cats-list ${scrollClass}`}>
              <div
                className={`library-filter-cat library-filter-untagged${hovered.id === UNTAGGED ? " is-hover" : ""}`}
                onMouseEnter={() => setHoverId(UNTAGGED)}
              >
                <button type="button" className="library-filter-cat-check" onClick={toggleUntagged}>
                  <Check all={draft[UNTAGGED] === "in"} mixed={false} />
                </button>
                <span className="library-filter-untagged-gap" />
                <span className="library-filter-cat-name">{t("study-list.library-tag.no-tags-added")}</span>
              </div>
              {view.map((category) => {
                const state = catState(category, draft);
                const empty = category.tagIds.length === 0;
                return (
                  <div
                    key={category.id}
                    className={`library-filter-cat${category.id === hovered.id ? " is-hover" : ""}${empty ? " is-empty" : ""}`}
                    onMouseEnter={() => setHoverId(category.id)}
                  >
                    <button
                      type="button"
                      className="library-filter-cat-check"
                      disabled={empty}
                      onClick={() => toggleCategory(category)}
                    >
                      <Check all={state.all} mixed={state.mixed} disabled={empty} />
                    </button>
                    <NameTip id={`library-filter-cat-${category.id}`} name={category.name} className="library-filter-cat-name" />
                    <span className="library-filter-cat-side">
                      {empty ? (
                        t("study-list.library-tag.no-tags")
                      ) : (
                        <>
                          <span>{category.tagIds.length}</span>
                          <Icon iconName="ChevronRight" />
                        </>
                      )}
                    </span>
                  </div>
                );
              })}
              </div>
            </div>
            <div className={`library-filter-menu-tags ${scrollClass}`} style={menuBackground}>
              {hovered.id === UNTAGGED ? (
                <p className="library-filter-untagged-note">{t("study-list.library-tag.no-tags-added-hint")}</p>
              ) : (
              <>
              {hovered.tagIds.length > 0 ? (
              <button
                type="button"
                className="library-filter-select-all"
                onClick={() => toggleCategory(hovered)}
              >
                <Check all={hoveredState.all} mixed={hoveredState.mixed} />
                {t("study-list.library-tag.select-all-tags")}
              </button>
              ) : null}
              {hoveredTags.length === 0 ? null : (
                hoveredTags.map((tag) => (
                  <div key={tag.id} className="library-filter-tag">
                    <button type="button" className="library-filter-tag-main" onClick={() => toggleInclude(tag.id)}>
                      <Check all={draft[tag.id] === "in"} mixed={false} />
                      <NameTip id={`library-filter-tag-${tag.id}`} name={tag.name} />
                    </button>
                  </div>
                ))
              )}
              </>
              )}
            </div>
            </div>
            <div className="library-filter-menu-footer" style={menuBackground}>
              <DefaultButton
                text={t("button.clear")}
                onClick={clearFilter}
                styles={{
                  root: {
                    backgroundColor: "transparent",
                    color: "var(--text-primary)",
                    border: "var(--global-border)",
                  },
                  rootHovered: {
                    backgroundColor: "transparent",
                    color: "var(--text-primary)",
                    border: "var(--global-border)",
                  },
                  rootPressed: {
                    backgroundColor: "transparent",
                    color: "var(--text-primary)",
                  },
                  label: { color: "var(--text-primary)", fontWeight: 600 },
                }}
              />
              <PrimaryButton
                text={t("button.apply")}
                onClick={applyFilter}
                className="common-default-active-button"
                styles={{ root: { marginLeft: 10 } }}
              />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export { LibraryCategoryFilter };
